import { useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Button, Text } from "@/components/ui";
import { deviceLabel, formatDeviceTime } from "@/lib/device-label";
import type { DeviceLimitStep } from "@/services/api/auth";

type DeviceChoiceFormProps = {
  step: DeviceLimitStep;
  /** Signs that device out and finishes the sign-in; rejects with a translated Error. */
  onChoose: (deviceId: string) => Promise<void>;
  onCancel: () => void;
};

/**
 * H3 device limit: the student is already signed in on `deviceLimit` devices. They pick one to
 * sign out (it is signed out at once), or cancel and nothing is created.
 */
export function DeviceChoiceForm({ step, onChoose, onCancel }: DeviceChoiceFormProps) {
  const t = useTranslations("auth.devices");
  const locale = useLocale();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function choose(deviceId: string) {
    setBusyId(deviceId);
    setError(null);
    try {
      await onChoose(deviceId);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : t("failed"));
      setBusyId(null);
    }
  }

  return (
    <div data-testid="device-choice">
      <Text className="mb-4" data-testid="device-choice-intro">
        {t("intro", { limit: step.deviceLimit })}
      </Text>
      <ul className="space-y-3">
        {step.devices.map((device) => {
          const label = deviceLabel(t, device);
          return (
            <li
              key={device.id}
              className="rounded-lg border border-[var(--border-strong)] p-3"
              data-testid={`device-choice-${device.id}`}
            >
              <p className="font-medium">{label}</p>
              <p className="text-xs text-[var(--text-muted)]">
                {t("lastActive", { time: formatDeviceTime(device.lastActiveAt, locale) })} ·{" "}
                {t("signedInAt", { time: formatDeviceTime(device.signedInAt, locale) })}
                {device.place ? ` · ${device.place}` : ""}
              </p>
              <Button
                type="button"
                variant="accent"
                className="mt-2"
                disabled={busyId !== null}
                aria-label={t("signOutLabel", { device: label })}
                data-testid={`device-choice-sign-out-${device.id}`}
                onClick={() => void choose(device.id)}
              >
                {busyId === device.id ? t("working") : t("signOut")}
              </Button>
            </li>
          );
        })}
      </ul>
      {error && (
        <Text role="alert" className="mt-4 text-[var(--danger)]" data-testid="device-choice-error">
          {error}
        </Text>
      )}
      <button
        type="button"
        className="mt-4 text-sm text-[var(--accent-ink)] underline underline-offset-4"
        data-testid="device-choice-cancel"
        disabled={busyId !== null}
        onClick={onCancel}
      >
        {t("cancel")}
      </button>
    </div>
  );
}
